
const environments = ["dev", "staging", "prod"] as const;

export type Environment = (typeof environments)[number];

/** Rendered wherever an environment is offered — never hardcode the list. */
export const environmentLabels: Record<
  Environment,
  { short: string; long: string }
> = {
  dev: { short: "Dev", long: "Development" },
  staging: { short: "Staging", long: "Staging" },
  prod: { short: "Prod", long: "Production" },
};

const fallbackSeatManagementEnvironments: Environment[] = ["dev", "staging"];

function isEnvironment(value: string): value is Environment {
  return environments.includes(value as Environment);
}

function parseEnvironmentList(
  value: string | undefined,
  fallback: Environment[],
) {
  const parsedEnvironments = (value ?? "")
    .split(",")
    .map((environment) => environment.trim())
    .filter(isEnvironment);

  return new Set(parsedEnvironments.length > 0 ? parsedEnvironments : fallback);
}

const apiBaseUrl = (
  import.meta.env.VITE_API_URL || "https://aapi.bayescase.com"
).replace(/\/$/, "");

/** The environments this admin deployment can reach (the stage's `envs` list). */
// Fails CLOSED to ["dev"]: a missing variable must not expose Staging and Prod tabs.
const availableEnvironments = parseEnvironmentList(
  import.meta.env.VITE_ENVIRONMENTS,
  ["dev"],
);

/** The safest environment this deployment can reach — dev whenever possible. */
const safestEnvironment: Environment = availableEnvironments.has("dev")
  ? "dev"
  : ([...availableEnvironments][0] ?? "dev");

function getDefaultEnvironment(): Environment {
  const stored = localStorage.getItem("selectedEnvironment");
  if (stored && isEnvironment(stored) && availableEnvironments.has(stored)) {
    return stored;
  }
  // A stored selection out of reach falls back to the safest reachable environment.
  return safestEnvironment;
}

/** Where this portal's copy dialog copies from and to: prod's reaches dev and staging too, the others only themselves. */
const copyEnvironments = parseEnvironmentList(
  import.meta.env.VITE_COPY_ENVIRONMENTS,
  [...availableEnvironments],
);

const seatManagementEnvironments = parseEnvironmentList(
  import.meta.env.VITE_SEAT_MANAGEMENT_ENVIRONMENTS,
  fallbackSeatManagementEnvironments,
);

const aiFeedbackEnvironments = parseEnvironmentList(
  import.meta.env.VITE_AI_FEEDBACK_ENVIRONMENTS,
  [],
);

/** Where the AI Evals page exists: dev and staging portals, never prod. */
const evalEnvironments = parseEnvironmentList(import.meta.env.VITE_EVAL_ENVIRONMENTS, []);

const maintenanceEnvironments = parseEnvironmentList(
  import.meta.env.VITE_MAINTENANCE_ENVIRONMENTS,
  [],
);

export const config = {
  // Cognito configuration
  cognito: {
    userPoolId: import.meta.env.VITE_USER_POOL_ID || "",
    // Must belong to userPoolId: Cognito routes sign-in by client id alone.
    userPoolClientId: import.meta.env.VITE_CLIENT_ID || "",
  },

  // Which environment the dashboard STARTS on (restores the last selection).
  defaultEnvironment: getDefaultEnvironment(),

  /** Write-target default, deliberately not defaultEnvironment: prod must never be pre-filled. */
  safestEnvironment,

  /** What the environment selector may offer — this deployment's reach. */
  environments: [...availableEnvironments],

  copyEnvironments: [...copyEnvironments],

  getApiUrl: (env: Environment = "dev") => {
    // Single API with environment-based paths
    return `${apiBaseUrl}/${env}`;
  },

  supportsSeatManagement: (env: Environment = "dev") =>
    seatManagementEnvironments.has(env),

  supportsAiFeedback: (env: Environment = "dev") =>
    aiFeedbackEnvironments.has(env),

  supportsMaintenance: (env: Environment = "dev") =>
    maintenanceEnvironments.has(env),

  supportsEvals: (env: Environment = "dev") => evalEnvironments.has(env),
};
